const unitPbgid = require('../config/unit_pbgid.json')

// pbgid -> { key, icon, age } for every unit variation (archer-2, archer-3, ...)
const unitsByPbgid = {}
// kebab unit key -> variations, used when a build order entry has no pbgid
const unitsByKey = {}

for (const [key, variations] of Object.entries(unitPbgid)) {
	unitsByKey[key] = variations
	for (const variation of variations) {
		unitsByPbgid[variation.pbgid] = { key, icon: variation.icon, age: variation.age ?? 1 }
	}
}

// Economy / support units are never part of the unit composition
const ECONOMY_UNITS = new Set([
	'villager',
	'gilded-villager',
	'jeanne-darc-peasant',
	'fishing-boat',
	'lodya-fishing-boat',
	'hunting-canoe',
	'trader',
	'trade-caravan',
	'trade-ship',
	'lodya-trade-ship',
	'venetian-trader',
	'transport-ship',
	'lodya-transport-ship',
	'imperial-official',
	'scholar',
	'worker-elephant',
	'rus-tribute',
])

/** "man-at-arms" -> "Man At Arms" */
function prettyName(key) {
	return key
		.split('-')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ')
}

/** Build order entries only carry an icon path like "races/english/units/man_at_arms_2" */
function keyFromIcon(icon) {
	const last = String(icon ?? '')
		.toLowerCase()
		.split('/')
		.pop()
	if (!last) return null

	const key = last.replace(/_\d+$/, '').replace(/_/g, '-')
	return unitsByKey[key] ? key : null
}

function lookupUnit(item) {
	const byPbgid = unitsByPbgid[item.pbgid]
	if (byPbgid) return byPbgid

	const key = keyFromIcon(item.icon)
	if (!key) return null

	// no pbgid match, fall back to the lowest age variation of that unit
	const variation = unitsByKey[key][0]
	return { key, icon: variation.icon, age: variation.age ?? 1 }
}

/**
 * Total military units produced during the game, most produced first.
 * Age variations are merged (archer-2 + archer-3 = archer) and the icon of the
 * highest age variation the player actually made is kept.
 * Returns [{ key, name, count, icon }]
 */
export function convertUnitComp(player) {
	const totals = new Map()

	for (const item of player?.buildOrder ?? []) {
		const unit = lookupUnit(item)
		if (!unit || ECONOMY_UNITS.has(unit.key)) continue

		const produced = (item.finished?.length ?? 0) + (item.constructed?.length ?? 0) + (item.unknown?.['14']?.length ?? 0)
		if (!produced) continue

		const entry = totals.get(unit.key) ?? { key: unit.key, name: prettyName(unit.key), count: 0, icon: unit.icon, age: unit.age }
		entry.count += produced
		if (unit.age >= entry.age) {
			entry.icon = unit.icon
			entry.age = unit.age
		}
		totals.set(unit.key, entry)
	}

	return [...totals.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)).map(({ key, name, count, icon }) => ({ key, name, count, icon }))
}
